import {
  typedCollection,
  typedDoc,
  getDocument,
  getCollection,
  where,
  orderBy,
} from '@/lib/firebase/firestore'
import type {
  PressKitConfig,
  PressKitStatDocument,
  PressKitAssetDocument,
  PressKitReleaseDocument,
  PressKitCoverageDocument,
} from '@/types'
import type {
  PressKitStatWithId,
  PressKitAssetWithId,
  PressKitReleaseWithId,
  PressKitCoverageWithId,
} from '../types'

// ─── Collection/doc refs ────────────────────────────────────────────────────

export const configRef = () => typedDoc<PressKitConfig>('pressKit', 'config')
export const statsCol = () => typedCollection<PressKitStatDocument>('pressKitStats')
export const assetsCol = () => typedCollection<PressKitAssetDocument>('pressKitAssets')
export const releasesCol = () => typedCollection<PressKitReleaseDocument>('pressKitReleases')
export const coverageCol = () => typedCollection<PressKitCoverageDocument>('pressKitCoverage')

// ─── Derived stat cache ─────────────────────────────────────────────────────
// Strategy: read the `pressKit/statCache` doc (public-readable). If it's fresh
// (< 24h), use it. Otherwise call the `refreshPressKitStatCache` Cloud Function,
// which recomputes server-side and updates the cache.

const STALE_MS = 24 * 60 * 60 * 1000

interface StatCacheDoc {
  values?: Record<string, string>
  refreshedAt?: import('firebase/firestore').Timestamp
}

export async function fetchDerivedStats(): Promise<Record<string, string>> {
  const cache = await getDocument(typedDoc<StatCacheDoc>('pressKit', 'statCache'))
  const refreshedAt = cache?.refreshedAt?.toMillis?.() ?? 0
  if (cache?.values && Date.now() - refreshedAt < STALE_MS) {
    return cache.values
  }
  const { refreshPressKitStatCacheFn } = await import('@/lib/firebase/functions')
  try {
    const result = await refreshPressKitStatCacheFn({})
    return result.data.values
  } catch (err) {
    // If the refresh fails, fall back to whatever the cache has (possibly stale).
    console.warn('[fetchDerivedStats] refresh failed, using stale cache', err)
    return cache?.values ?? {}
  }
}

// ─── Public service ─────────────────────────────────────────────────────────

export const pressKitService = {
  async getConfig(): Promise<PressKitConfig | null> {
    try {
      return await getDocument(configRef())
    } catch (error) {
      // An unpublished config is intentionally unreadable to the public. Treat
      // that expected denial as the same unavailable state the page already
      // renders for a missing or unpublished config.
      if (
        error instanceof Error &&
        'code' in error &&
        (error as { code?: unknown }).code === 'permission-denied'
      ) {
        return null
      }
      throw error
    }
  },

  async getVisibleStats(): Promise<PressKitStatWithId[]> {
    return getCollection(statsCol(), where('isVisible', '==', true), orderBy('order', 'asc'))
  },

  async getVisibleAssets(): Promise<PressKitAssetWithId[]> {
    return getCollection(assetsCol(), where('isVisible', '==', true), orderBy('order', 'asc'))
  },

  async getPublishedReleases(): Promise<PressKitReleaseWithId[]> {
    return getCollection(
      releasesCol(),
      where('isPublished', '==', true),
      orderBy('publishedAt', 'desc')
    )
  },

  async getVisibleCoverage(): Promise<PressKitCoverageWithId[]> {
    return getCollection(coverageCol(), where('isVisible', '==', true), orderBy('order', 'asc'))
  },
}
